import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { orderBy } from "lodash";
import TextInputField from "../Shared/TextInputField";
import SelectField from "components/Shared/SelectField";
import Textarea from "components/Shared/Textarea";
import { TITLE_OPTIONS } from "../../constants";
import CheckboxField from "../Shared/CheckboxField";
import { selectActivePractitioners } from "../appSlice";
import { getPractitionerProfile, getProfileSpecialities, savePractitionerProfile } from "../../services/practitioner";

const BREAKER = "\n";

const PractitionerProfile = () => {
  const practitioners = useSelector(selectActivePractitioners);
  const sites = useSelector((state) => state.app.sites);

  const [sortedPractitioners, setSortedPractitioners] = useState([]);
  const [selectedPractitionerId, setSelectedPractitionerId] = useState(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [specialityOptions, setSpecialityOptions] = useState([]);
  const [specialtiesObj, setSpecialtiesObj] = useState({});
  const [selectedSpecialtyIds, setSelectedSpecialtyIds] = useState([]);
  const [title, setTitle] = useState("");
  const [qualification, setQualification] = useState("");
  const [language, setLanguage] = useState("");
  const [profile, setProfile] = useState([]);
  const [skills, setSkills] = useState([]);
  const [avatarLink, setAvatarLink] = useState("");
  const [profileLink, setProfileLink] = useState("");
  const [specialistRules, setSpecialistRules] = useState("");
  const [publishedTo, setPublishedTo] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fee, setFee] = useState({});

  useEffect(() => {
    getProfileSpecialities().then((specialities) => {
      const options = [];
      const specialtiesFromDBObj = {};
      specialities.forEach((speciality) => {
        const { id } = speciality;
        options.push({ value: id, label: speciality.name });
        specialtiesFromDBObj[id] = speciality;
      });
      setSpecialityOptions(options);
      setSpecialtiesObj(specialtiesFromDBObj);
    });
  }, []);

  useEffect(() => {
    if (selectedPractitionerId) {
      setFirstName("");
      setLastName("");
      setSelectedSpecialtyIds([]);
      setTitle("");
      setQualification("");
      setLanguage("");
      setSkills("");
      setSpecialistRules("");
      setProfile("");
      setAvatarLink("");
      setProfileLink("");
      setFee({});
    }
  }, [selectedPractitionerId]);

  useEffect(() => {
    if (Array.isArray(practitioners) && practitioners.length > 0) {
      setSortedPractitioners(orderBy(practitioners, ["firstName", "lastName"], ["asc", "asc"]));
    }
  }, [practitioners]);

  const handleClickPractitioner = (practitioner) => {
    setSelectedPractitionerId(practitioner.practitionerId);

    getPractitionerProfile(practitioner.practitionerId).then((data) => {
      // A practitioner with no profile gives null, so the next line throws and the form stays
      // empty. The page has done that since the modular SDK made the old doc.exists check always
      // true, so the else branch that filled in the names from the list never ran.
      setFirstName(data.firstName);
      setLastName(data.lastName);
      setSelectedSpecialtyIds(data.specialties.map(({ id }) => id));
      setTitle(data.title || "");
      setQualification(data.qualification || "");
      setLanguage(data.language || "");
      setSkills(data.skills.length > 0 ? data.skills.join(BREAKER) : "");
      setProfile(data.profile.join("\n\n"));
      setAvatarLink(data.avatarLink || "");
      setProfileLink(data.profileLink || "");
      setSpecialistRules(data?.specialistRules?.length > 0 ? data?.specialistRules?.join(BREAKER) : "");
      setPublishedTo(data?.publishedTo || []);
      setFee(data.fee || {});
    });
  };

  const handleSpecialityChange = (e) => {
    const selectedSpecialtyIds = [];
    // convert HTML select options node list to array
    const options = [...e.target.options];
    options.forEach((o) => {
      if (o.selected) {
        selectedSpecialtyIds.push(o.value);
      }
    });
    setSelectedSpecialtyIds(selectedSpecialtyIds);
  };

  const handleSubmit = () => {
    if (!selectedPractitionerId) {
      return;
    }

    setIsSubmitting(true);

    const specialities = selectedSpecialtyIds.map((id) => specialtiesObj[id].name);
    const specialties = selectedSpecialtyIds.map((id) => specialtiesObj[id]);

    const data = {
      firstName,
      lastName,
      specialities,
      specialties,
      title,
      qualification,
      language,
      skills: skills.split(BREAKER).filter((s) => !!s),
      profile: profile.split(BREAKER).filter((p) => !!p),
      avatarLink,
      profileLink,
      specialistRules: specialistRules.split(BREAKER).filter((r) => !!r),
      publishedTo,
      fee,
    };
    savePractitionerProfile(selectedPractitionerId, data).then(() => setIsSubmitting(false));
  };

  const isPublishedToSite = (site) => {
    return publishedTo.filter((siteBeenPublishedTo) => siteBeenPublishedTo.id === site.id).length > 0;
  };

  const handleFeeChange = (e, cb) => {
    const { value } = e.target;
    if (/^\d*\.?\d*$/.test(value)) cb(value);
  };

  return (
    <div className="columns">
      <div className="column is-one-third">
        <TableWrapper>
          <table className="table is-striped is-hoverable is-size-7">
            <tbody>
              {sortedPractitioners.map((p) => (
                <StyledTr
                  key={p.practitionerId}
                  onClick={() => handleClickPractitioner(p)}
                  className={p.practitionerId === selectedPractitionerId ? "has-background-primary-light" : ""}
                >
                  <td>{`${p.firstName} ${p.lastName}`}</td>
                </StyledTr>
              ))}
            </tbody>
          </table>
        </TableWrapper>
      </div>
      <div className="column is-two-thirds">
        <div className="columns">
          <div className="column">
            <TextInputField
              label="First name"
              isRequired
              value={firstName}
              onChange={(e) => setFirstName(e.target.value.trim())}
              hasError={!firstName}
            />
          </div>
          <div className="column">
            <TextInputField
              label="Last name"
              isRequired
              value={lastName}
              onChange={(e) => setLastName(e.target.value.trim())}
              hasError={!lastName}
            />
          </div>
        </div>
        <div className="columns">
          <div className="column">
            <SelectField
              label="Specialities"
              isRequired
              isMultiple
              size={4}
              value={selectedSpecialtyIds}
              options={specialityOptions}
              onChange={handleSpecialityChange}
            />
          </div>
        </div>
        <div className="columns">
          <div className="column">
            <SelectField
              label="Title"
              value={title}
              options={TITLE_OPTIONS}
              onChange={(e) => {
                setTitle(e.target.value);
              }}
            />
          </div>
        </div>
        <div className="columns">
          <div className="column">
            <TextInputField
              label="Qualification"
              value={qualification}
              onChange={(e) => setQualification(e.target.value)}
              placeholder="e.g MBBS, DNB, FANZCA, FFPMANZCA, PG Dipl Clinical Research"
            />
          </div>
        </div>
        <div className="columns">
          <div className="column">
            <TextInputField
              label="Language"
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              placeholder="e.g English, Hindi, Mandarin"
            />
          </div>
        </div>
        <div className="columns">
          <div className="column">
            <Textarea
              label="Skills"
              rows={5}
              value={skills}
              onChange={(e) => setSkills(e.target.value)}
              placeholder="Place multiple skills in newlines."
            />
          </div>
        </div>
        <div className="columns">
          <div className="column">
            <Textarea
              label="Profile"
              rows={10}
              value={profile}
              isRequired
              hasError={!profile}
              onChange={(e) => setProfile(e.target.value)}
            />
          </div>
        </div>
        <div className="columns">
          <div className="column">
            <TextInputField label="Avatar link" value={avatarLink} onChange={(e) => setAvatarLink(e.target.value)} />
          </div>
        </div>
        <div className="columns">
          <div className="column">
            <TextInputField label="Profile link" value={profileLink} onChange={(e) => setProfileLink(e.target.value)} />
          </div>
        </div>
        <div className="columns">
          <div className="column">
            <Textarea
              label="Specialist Rules"
              rows={5}
              value={specialistRules}
              onChange={(e) => setSpecialistRules(e.target.value)}
              placeholder="Place multiple rules in newlines."
            />
          </div>
        </div>
        <label className="label is-small">{"Published To"}</label>
        <div className="columns">
          {sites.map((site) => {
            return (
              <div className="column" key={site.id}>
                <CheckboxField
                  label={site.siteName}
                  checked={isPublishedToSite(site)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setPublishedTo([...publishedTo, site]);
                    } else {
                      setPublishedTo(publishedTo.filter((siteBeenPublishedTo) => siteBeenPublishedTo.id !== site.id));
                    }
                  }}
                />
              </div>
            );
          })}
        </div>
        <label className="label is-small">{"New Appt Fee"}</label>
        <div className="columns">
          <div className="column">
            <TextInputField
              isRequired={true}
              label="Total"
              value={fee.initial?.total || ""}
              onChange={(e) =>
                handleFeeChange(e, (value) =>
                  setFee((prev) => ({ ...prev, initial: { ...prev.initial, total: value } }))
                )
              }
              hasError={!fee.initial?.total}
              placeholder="Integer and decimal only."
            />
          </div>
          <div className="column">
            <TextInputField
              isRequired={true}
              label="Out of Pocket"
              value={fee.initial?.outOfPocket || ""}
              onChange={(e) =>
                handleFeeChange(e, (value) =>
                  setFee((prev) => ({ ...prev, initial: { ...prev.initial, outOfPocket: value } }))
                )
              }
              hasError={!fee.initial?.outOfPocket || ""}
              placeholder="Integer and decimal only."
            />
          </div>
          <div className="column">
            <TextInputField
              isRequired={true}
              label="Medicare Rebate"
              value={fee.initial?.medicareRebate || ""}
              onChange={(e) =>
                handleFeeChange(e, (value) =>
                  setFee((prev) => ({ ...prev, initial: { ...prev.initial, medicareRebate: value } }))
                )
              }
              hasError={!fee.initial?.medicareRebate}
              placeholder="Integer and decimal only."
            />
          </div>
        </div>
        <label className="label is-small">{"Review Appt Fee"}</label>
        <div className="columns">
          <div className="column">
            <TextInputField
              isRequired={true}
              label="Total"
              value={fee.review?.total || ""}
              onChange={(e) =>
                handleFeeChange(e, (value) => setFee((prev) => ({ ...prev, review: { ...prev.review, total: value } })))
              }
              hasError={!fee.review?.total || ""}
              placeholder="Integer and decimal only."
            />
          </div>
          <div className="column">
            <TextInputField
              isRequired={true}
              label="Out of Pocket"
              value={fee.review?.outOfPocket || ""}
              onChange={(e) =>
                handleFeeChange(e, (value) =>
                  setFee((prev) => ({ ...prev, review: { ...prev.review, outOfPocket: value } }))
                )
              }
              hasError={!fee.review?.outOfPocket}
              placeholder="Integer and decimal only."
            />
          </div>
          <div className="column">
            <TextInputField
              isRequired={true}
              label="Medicare Rebate"
              value={fee.review?.medicareRebate || ""}
              onChange={(e) =>
                handleFeeChange(e, (value) =>
                  setFee((prev) => ({ ...prev, review: { ...prev.review, medicareRebate: value } }))
                )
              }
              hasError={!fee.review?.medicareRebate}
              placeholder="Integer and decimal only."
            />
          </div>
        </div>
        <div className="columns">
          <div className="column">
            <button
              className={`${isSubmitting ? "is-loading " : ""}button is-small is-info`}
              disabled={!firstName || !lastName || selectedSpecialtyIds.length === 0 || !profile || isSubmitting}
              onClick={handleSubmit}
            >
              Submit
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const StyledTr = styled.tr`
  cursor: pointer;
`;

const TableWrapper = styled.div`
  max-height: 640px;
  overflow-y: scroll;
`;

export default PractitionerProfile;
